// src/components/common/PhotoUpload.jsx
// ✅ Componente riusabile per upload foto da telefono/desktop
// ✅ Preview immediata, drag & drop, compressione client-side
import React, { useState, useRef, useCallback } from 'react';
import { CameraIcon, PhotoIcon, XMarkIcon, ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline';
import axiosConfig from '../../api/axiosConfig';
import toast from 'react-hot-toast';

const MAX_PHOTOS = 5;

const PhotoUpload = ({
    entityType,   // 'expenses' | 'projects'
    entityId,
    existingPhotos = [],
    onPhotosChange,
    compact = false,
}) => {
    const [photos, setPhotos] = useState(existingPhotos);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    const handleUpload = useCallback(async (files) => {
        if (!files?.length) return;
        if (photos.length >= MAX_PHOTOS) {
            toast.error(`Massimo ${MAX_PHOTOS} foto per elemento`);
            return;
        }

        const file = files[0];
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File troppo grande. Massimo 10MB.');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('photo', file);

            const res = await axiosConfig.post(
                `/upload/${entityType}/${entityId}/photos`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            const newPhotos = res.data.data.photos;
            setPhotos(newPhotos);
            onPhotosChange?.(newPhotos);
            toast.success('Foto caricata!');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore upload foto');
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    }, [photos, entityType, entityId, onPhotosChange]);

    const handleDelete = async (url) => {
        try {
            await axiosConfig.delete(`/upload/${entityType}/${entityId}/photos`, { data: { url } });
            const updated = photos.filter(p => p !== url);
            setPhotos(updated);
            onPhotosChange?.(updated);
            toast.success('Foto eliminata');
        } catch {
            toast.error('Errore eliminazione foto');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleUpload(e.dataTransfer.files);
    };

    if (compact) {
        return (
            <div className="flex items-center gap-2 flex-wrap">
                {photos.map((url, i) => (
                    <div key={i} className="relative group">
                        <img src={url} alt={`foto ${i + 1}`}
                            className="w-12 h-12 object-cover rounded-lg border border-gray-200 cursor-pointer"
                            onClick={() => setPreview(url)} />
                        <button onClick={() => handleDelete(url)}
                            className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full items-center justify-center text-xs hidden group-hover:flex">
                            ×
                        </button>
                    </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                    <button onClick={() => inputRef.current?.click()} disabled={uploading}
                        className="w-12 h-12 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center hover:border-blue-400 hover:bg-blue-50 transition">
                        {uploading
                            ? <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin" />
                            : <CameraIcon className="w-4 h-4 text-gray-400" />
                        }
                    </button>
                )}
                <input ref={inputRef} type="file" accept="image/*" capture="environment"
                    className="hidden" onChange={e => handleUpload(e.target.files)} />
                {preview && <PhotoPreviewModal url={preview} onClose={() => setPreview(null)} />}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Zona upload */}
            <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => !uploading && photos.length < MAX_PHOTOS && inputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-6 text-center transition cursor-pointer ${dragOver ? 'border-blue-400 bg-blue-50' :
                        photos.length >= MAX_PHOTOS ? 'border-gray-100 bg-gray-50 cursor-not-allowed' :
                            'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}>
                {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                        <ArrowPathIcon className="w-8 h-8 text-blue-500 animate-spin" />
                        <p className="text-sm text-blue-600 font-medium">Caricamento in corso...</p>
                    </div>
                ) : photos.length >= MAX_PHOTOS ? (
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                        <PhotoIcon className="w-8 h-8" />
                        <p className="text-sm">Limite {MAX_PHOTOS} foto raggiunto</p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2">
                        <div className="flex gap-3">
                            <CameraIcon className="w-8 h-8 text-gray-400" />
                            <PhotoIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-600">
                            Scatta una foto o carica dal dispositivo
                        </p>
                        <p className="text-xs text-gray-400">
                            JPG, PNG, WebP · Max 10MB · {photos.length}/{MAX_PHOTOS} foto
                        </p>
                        <div className="flex gap-2 mt-2">
                            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-lg font-medium">
                                📷 Fotocamera
                            </span>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg font-medium">
                                🖼️ Galleria
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Input file — capture=environment per aprire fotocamera su mobile */}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={e => handleUpload(e.target.files)}
            />

            {/* Griglia foto esistenti */}
            {photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {photos.map((url, i) => (
                        <div key={i} className="relative group aspect-square">
                            <img src={url} alt={`Foto ${i + 1}`}
                                className="w-full h-full object-cover rounded-xl border border-gray-200" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-xl transition flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                                <button onClick={() => setPreview(url)}
                                    className="w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-gray-100">
                                    <EyeIcon className="w-4 h-4 text-gray-700" />
                                </button>
                                <button onClick={() => handleDelete(url)}
                                    className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center shadow-sm hover:bg-red-600">
                                    <XMarkIcon className="w-4 h-4 text-white" />
                                </button>
                            </div>
                            {i === 0 && (
                                <span className="absolute top-1 left-1 text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded-md font-medium">
                                    Principale
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Preview modale */}
            {preview && <PhotoPreviewModal url={preview} onClose={() => setPreview(null)} />}
        </div>
    );
};

// ==================== MODAL PREVIEW ====================
const PhotoPreviewModal = ({ url, onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative max-w-3xl max-h-[85vh]">
            <img src={url} alt="Preview" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" />
            <button onClick={onClose}
                className="absolute top-2 right-2 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition">
                <XMarkIcon className="w-5 h-5 text-white" />
            </button>
        </div>
    </div>
);

export default PhotoUpload;
export { PhotoPreviewModal };